import { useState, useEffect, useCallback } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Shield, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface ChatbotConsentBannerProps {
  onConsentGranted: () => void;
}

export const ChatbotConsentBanner = ({ onConsentGranted }: ChatbotConsentBannerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkChatbotConsent = useCallback(async () => {
    if (!user) {
      setIsVisible(true);
      return;
    }

    try {
      const userId = user.id || '11111111-2222-3333-4444-555555555555';
      const { data, error } = await supabase
        .from('user_consent')
        .select('chatbot_consent')
        .eq('user_id', userId)
        .eq('consent_version', 'v1.0')
        .maybeSingle();

      if (error) {
        console.error('Error checking chatbot consent:', error);
        setIsVisible(true);
        return;
      }

      if (!data || !data.chatbot_consent) {
        setIsVisible(true);
      } else {
        onConsentGranted();
      }
    } catch (error) {
      console.error('Error checking consent:', error);
      setIsVisible(true);
    }
  }, [user, onConsentGranted]);

  useEffect(() => {
    checkChatbotConsent();
  }, [checkChatbotConsent]);

  const handleGrantConsent = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const userId = user.id || '11111111-2222-3333-4444-555555555555';
      const { error } = await supabase
        .from('user_consent')
        .update({ chatbot_consent: true })
        .eq('user_id', userId)
        .eq('consent_version', 'v1.0');

      if (error) throw error;

      toast({
        title: "Consent granted",
        description: "You can now use the AI chatbot",
      });

      setIsVisible(false);
      onConsentGranted();
    } catch (error) {
      console.error('Error granting consent:', error);
      toast({
        title: "Error",
        description: "Failed to save consent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <Alert className="mb-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
      <Shield className="h-4 w-4 text-blue-600" />
      <AlertDescription className="flex items-start justify-between gap-4">
        <div className="flex-1 text-sm text-blue-800 dark:text-blue-200">
          <p className="font-semibold mb-2">AI Chatbot Privacy Notice</p>
          <p className="mb-3">
            Our AI chatbot uses OpenAI and Google Gemini to process your messages and provide energy assistance. 
            Your chat messages will be sent to these third-party AI providers for processing.
          </p>
          <ul className="list-disc pl-5 mb-3 space-y-1">
            <li>Messages are processed by AI services (OpenAI/Google Gemini)</li>
            <li>Chat history is stored securely in your account</li>
            <li>You can export or delete your chat data anytime in Settings</li>
            <li>You can revoke this consent in Settings → Privacy & Data</li>
          </ul>
          <div className="flex gap-2">
            <Button 
              onClick={handleGrantConsent} 
              disabled={isLoading}
              size="sm"
            >
              {isLoading ? "Saving..." : "Accept & Continue"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setIsVisible(false)}
              size="sm"
            >
              Decline
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
